import { renderMarkdown } from './markdown';

/**
 * The two flavours of one copied reply.
 *
 * Writing both is the whole point: the clipboard holds several representations at once and the
 * paste target picks the richest it understands. Outlook, Gmail, Teams and Word take `text/html`
 * and keep the bold, the links and the table; a code editor or a terminal takes `text/plain`.
 * Writing only plain text - which is what this used to do, with the raw Markdown - meant every
 * target got literal `**` and pipe-delimited table rows.
 */
export interface AiChatClipboardPayload {
  html: string;
  text: string;
}

/**
 * Inline styles for the copied table. The site's stylesheet does not travel with the clipboard,
 * so without these a pasted table arrives as unruled rows of text with everything jammed
 * together. Deliberately plain and self-contained - no colour beyond a grey rule, since the
 * reply is being pasted into someone else's document, not ours.
 */
const TABLE_STYLE = 'border-collapse:collapse;border:1px solid #d9d9d9;';
// No `text-align` here - it is appended per cell from the column's own alignment, so a price
// column pastes right-aligned the way it reads on screen. A blanket `text-align:left` in this
// constant silently overrode it, because the .md-align-* classes carry no weight once the
// fragment has left our stylesheet behind.
const CELL_STYLE = 'border:1px solid #d9d9d9;padding:6px 10px;vertical-align:top;';
const HEADER_CELL_STYLE = `${CELL_STYLE}font-weight:600;background:#f5f5f5;`;

/**
 * Turns an assistant reply's Markdown into a rich + plain clipboard pair.
 *
 * Browser-only: it renders into a detached element to walk the result. Callers already guard on
 * isPlatformBrowser.
 *
 * @param markdown the reply exactly as the agent sent it
 */
export function buildAiChatClipboardPayload(markdown: string): AiChatClipboardPayload {
  const source = (markdown ?? '').trim();
  if (!source) {
    return { html: '', text: '' };
  }

  // renderMarkdown escapes and sanitises, so this is the same HTML the bubble displays.
  const root = document.createElement('div');
  root.innerHTML = renderMarkdown(source);

  applyInlineTableStyles(root);

  return {
    // The charset meta is what stops Outlook mangling non-ASCII (a "×" in a size, a curly
    // apostrophe) into mojibake when it reads the fragment.
    html: `<meta charset="utf-8">${root.innerHTML}`,
    text: toPlainText(root)
  };
}

/** Pushes the table rules inline, so they survive the trip into another application. */
function applyInlineTableStyles(root: HTMLElement): void {
  root.querySelectorAll('table').forEach((table) => {
    table.setAttribute('style', TABLE_STYLE);
  });
  root.querySelectorAll('th').forEach((cell) => {
    cell.setAttribute('style', `${HEADER_CELL_STYLE}${alignmentStyle(cell)}`);
  });
  root.querySelectorAll('td').forEach((cell) => {
    cell.setAttribute('style', `${CELL_STYLE}${alignmentStyle(cell)}`);
  });
}

/**
 * The inline `text-align` for one cell, read back off the class `renderMarkdown` put there.
 *
 * The alignment is real data - it is the `---:` the agent wrote in its delimiter row to mark a
 * column as figures - and it is the difference between a column of prices that reads as a
 * column and one that staggers. On screen the class does the work; in a pasted fragment only an
 * inline style survives, so it is restated here rather than left to a stylesheet that is not
 * coming along.
 */
function alignmentStyle(cell: Element): string {
  if (cell.classList.contains('md-align-right')) {
    return 'text-align:right;';
  }
  if (cell.classList.contains('md-align-center')) {
    return 'text-align:center;';
  }
  return 'text-align:left;';
}

/**
 * Builds the plain-text flavour from the rendered HTML rather than from the Markdown, so it is
 * the reply as read - no `**`, no `|` rules - regardless of what syntax produced it.
 *
 * Table cells are separated by tabs and rows by newlines, which is the format a spreadsheet
 * understands: pasting a quotes table into Excel lands one value per cell rather than one long
 * line per row.
 */
function toPlainText(root: HTMLElement): string {
  const blocks: string[] = [];

  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').trim();
      if (text) {
        blocks.push(text);
      }
      return;
    }

    if (!(node instanceof HTMLElement)) {
      return;
    }

    const tag = node.tagName.toLowerCase();

    // The renderer wraps every table in a `.md-table-scroll` div, so the <table> is a
    // GRANDCHILD here, not a child. Matching only `tag === 'table'` missed it, the node fell
    // through to the `textContent` branch at the bottom, and `collapse()` flattened the whole
    // grid into one run-on string - "ItemQtyIRONWOOD Battens12Delivery1" - losing exactly the
    // tabs and newlines this function exists to produce. Pasting a quotes table into Excel
    // landed it in a single cell.
    //
    // Unwrapped by looking for the table rather than by testing the wrapper's class name, so
    // renaming or restyling the wrapper cannot quietly bring the bug back.
    const table = tag === 'table' ? node : node.querySelector('table');
    if (table instanceof HTMLElement) {
      blocks.push(tableToText(table));
      return;
    }

    if (tag === 'ul' || tag === 'ol') {
      blocks.push(listToText(node, tag === 'ol'));
      return;
    }

    const text = collapse(node.textContent);
    if (text) {
      blocks.push(text);
    }
  });

  return blocks.join('\n\n').trim();
}

function tableToText(table: HTMLElement): string {
  return Array.from(table.querySelectorAll('tr'))
    .map((row) => Array.from(row.querySelectorAll('th,td'))
      .map((cell) => collapse(cell.textContent))
      .join('\t'))
    .filter((row) => row.replace(/\t/g, '').length > 0)
    .join('\n');
}

function listToText(list: HTMLElement, ordered: boolean): string {
  // Only this list's own items - a nested list's are rendered by its own <li>'s textContent.
  return Array.from(list.children)
    .filter((child) => child.tagName.toLowerCase() === 'li')
    .map((item, index) => `${ordered ? `${index + 1}.` : '-'} ${collapse(item.textContent)}`)
    .join('\n');
}

/** Squeezes the whitespace the HTML source carries (indentation, wrapped lines) into single spaces. */
function collapse(value: string | null): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}
